import { useQuery } from '@tanstack/react-query'
import { useMatches, useNavigate, useSearch } from '@tanstack/react-router'
import { useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'
import { getSearchCasesQuery } from '@/lib/queries/business/v4/case/search/queries'

export const useApplicantCaseViewTabs = () => {
  const { applicantId } = useParams({
    from: '/_app/applicants/$applicantId',
  })
  const routeMatches = useMatches()
  const isInEoirCase = routeMatches.some(
    (match) => match.routeId === '/_app/applicants/$applicantId/cases-eoir/$caseNumber'
  )
  const [currentCaseViewTab, setCurrentCaseViewTab] = useState<'immigration' | 'eoir' | null>(
    isInEoirCase ? 'eoir' : null
  )
  const searchParams = useSearch({ from: '/_app/applicants/$applicantId' })
  const { sortKey, sortOrder } = searchParams

  // We fetch both immigration and EOIR cases to determine which tab should be initially selected
  // based on the available data for this applicant
  const { data: dataListCaseImmigration, isLoading: isLoadingCasesImmigration } = useQuery(
    getSearchCasesQuery({
      path_id: Number(applicantId),
      sort: sortKey,
      asc: sortOrder === 'asc',
      archiveStatus: CASE_ARCHIVE_STATUS_MAP.active,
    })
  )
  const { data: dataListCaseEoir, isLoading: isLoadingCasesEoir } = useQuery(
    getSearchCasesQuery({
      path_id: Number(applicantId),
      sort: sortKey,
      asc: sortOrder === 'asc',
      archiveStatus: CASE_ARCHIVE_STATUS_MAP.active,
      isEoir: true,
    })
  )

  const navigate = useNavigate({
    from: '/applicants/$applicantId',
  })

  const handleChangeCaseViewTab = useCallback(
    async (tab: 'immigration' | 'eoir') => {
      await navigate({
        to: '/applicants/$applicantId',
      })
      setCurrentCaseViewTab(tab)
    },
    [navigate]
  )

  useEffect(
    function determineInitialCaseViewTabBasedOnCaseLists() {
      if (currentCaseViewTab) {
        return
      }
      if (isLoadingCasesImmigration || isLoadingCasesEoir) {
        return
      }

      if (dataListCaseImmigration?.cases?.length && dataListCaseEoir?.cases?.length) {
        setCurrentCaseViewTab('immigration')
      } else if (dataListCaseEoir?.cases?.length) {
        setCurrentCaseViewTab('eoir')
      } else {
        setCurrentCaseViewTab('immigration')
      }
    },
    [dataListCaseImmigration, dataListCaseEoir, isLoadingCasesImmigration, isLoadingCasesEoir, currentCaseViewTab]
  )

  return {
    currentCaseViewTab,
    handleChangeCaseViewTab,
  }
}
